import { z } from "zod";
import {
  commandContextSchema,
  commandError,
  stableFingerprint,
  type AuditEntry,
  type AuditResult,
  type CommandContext,
  type CommandReceipt,
  type CommandResult,
  type CommandSuccess,
  type OutboxJob,
} from "../../lib/command-contract";
import type {
  ActivityEvent,
  Company,
  Contact,
  Lead,
  Membership,
  PartyChannel,
  Task,
} from "../../lib/types";
import { normalizeEmail } from "../identity/identity";
import { executeCreateTask } from "../tasks/commands";
import {
  COMMUNICATION_CHANNELS,
  DELIVERY_MODES,
  EMAIL_CHANNEL,
  MAILBOX_FOLDERS,
  type ChannelSuppression,
  type CommunicationMessage,
  type CommunicationThread,
  type DeliveryMode,
  type ChannelBox,
  type MailboxSyncCursor,
  type MessageAttachmentMeta,
  type MessageDelivery,
  type MessageState,
  type MessageTemplate,
  type MessageTemplateVersion,
  type SuppressionReason,
  TERMINAL_MESSAGE_STATES,
} from "./contracts";
import {
  DAILY_SEND_CAP,
  evaluateMailboxHealth,
  evaluateSendPolicy,
  findSuppression,
  localDayKey,
  outOfOfficeDecision,
  suppressionScopeFor,
  type DeliveryOutcome,
  type MailboxHealth,
  type OutOfOfficeAction,
  type SendBlockReason,
} from "./policies";
import {
  allowedHeaders,
  contentHash,
  findDuplicate,
  isAutoResponder,
  normalizeMessageId,
  normalizeSubject,
  parseAddress,
  parseReferences,
  resolveThreadLink,
  type DuplicateEvidence,
  type ThreadResolution,
} from "./threading";

/**
 * Comandos locais do canal de e-mail. Eles são a única fronteira de escrita:
 * a Inbox, o futuro runner e o futuro MCP devem chamá-los em vez de repetir
 * regras. Nenhum comando abre socket, envia mensagem ou fala com fornecedor.
 */

export interface CommunicationCommandState {
  mailboxes: ChannelBox[];
  mailboxCursors: MailboxSyncCursor[];
  threads: CommunicationThread[];
  messages: CommunicationMessage[];
  deliveries: MessageDelivery[];
  messageAttachments: MessageAttachmentMeta[];
  messageTemplates: MessageTemplate[];
  messageTemplateVersions: MessageTemplateVersion[];
  channelSuppressions: ChannelSuppression[];
  outboxJobs: OutboxJob[];
  commandReceipts: CommandReceipt[];
  auditLog: AuditEntry[];
  events: ActivityEvent[];
  tasks: Task[];
  leads: Lead[];
  companies: Company[];
  contacts: Contact[];
  partyChannels: PartyChannel[];
  memberships: Membership[];
}

export interface CommunicationCommandDeps {
  now: () => string;
  uuid: () => string;
}

type RuntimeRecord = Record<string, unknown>;

function asRecord(value: unknown): RuntimeRecord | undefined {
  try {
    if (value === null || typeof value !== "object" || Array.isArray(value)) return undefined;
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) return undefined;
    if (Object.getOwnPropertySymbols(value).length > 0) return undefined;

    const normalized = Object.create(null) as RuntimeRecord;
    for (const key of Object.getOwnPropertyNames(value)) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor?.enumerable || !("value" in descriptor)) return undefined;
      normalized[key] = descriptor.value;
    }
    return normalized;
  } catch {
    return undefined;
  }
}

function denseArray(value: unknown): unknown[] | undefined {
  try {
    if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) return undefined;
    if (Object.getOwnPropertySymbols(value).length > 0) return undefined;

    const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
    if (
      !lengthDescriptor ||
      !("value" in lengthDescriptor) ||
      !Number.isSafeInteger(lengthDescriptor.value) ||
      lengthDescriptor.value < 0
    )
      return undefined;
    const length = lengthDescriptor.value;
    const ownNames = Object.getOwnPropertyNames(value);
    if (ownNames.length !== length + 1 || !ownNames.includes("length")) return undefined;
    for (const key of ownNames) {
      if (key === "length") continue;
      if (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= length) return undefined;
    }

    const normalized: unknown[] = [];
    for (let index = 0; index < length; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
      if (!descriptor?.enumerable || !("value" in descriptor)) return undefined;
      normalized.push(descriptor.value);
    }
    return normalized;
  } catch {
    return undefined;
  }
}

const INVALID_COMMUNICATION_INPUT = Symbol("invalid_communication_input");

function normalizeJsonValue(
  value: unknown,
  seen: WeakSet<object>,
): unknown | typeof INVALID_COMMUNICATION_INPUT {
  try {
    if (value === null) return null;
    if (typeof value !== "object") {
      return typeof value === "function" || typeof value === "symbol" || typeof value === "bigint"
        ? INVALID_COMMUNICATION_INPUT
        : value;
    }
    if (seen.has(value)) return INVALID_COMMUNICATION_INPUT;
    seen.add(value);

    if (Array.isArray(value)) {
      const array = denseArray(value);
      if (!array) return INVALID_COMMUNICATION_INPUT;
      const normalized: unknown[] = [];
      for (const item of array) {
        const child = normalizeJsonValue(item, seen);
        if (child === INVALID_COMMUNICATION_INPUT) return INVALID_COMMUNICATION_INPUT;
        normalized.push(child);
      }
      seen.delete(value);
      return normalized;
    }

    const record = asRecord(value);
    if (!record) return INVALID_COMMUNICATION_INPUT;
    const normalized = Object.create(null) as RuntimeRecord;
    for (const key of Object.getOwnPropertyNames(record)) {
      const child = normalizeJsonValue(record[key], seen);
      if (child === INVALID_COMMUNICATION_INPUT) return INVALID_COMMUNICATION_INPUT;
      normalized[key] = child;
    }
    seen.delete(value);
    return normalized;
  } catch {
    return INVALID_COMMUNICATION_INPUT;
  }
}

function normalizeCommunicationJson(value: unknown): unknown | undefined {
  const normalized = normalizeJsonValue(value, new WeakSet<object>());
  return normalized === INVALID_COMMUNICATION_INPUT ? undefined : normalized;
}

export interface CommunicationExecution<T> {
  result: CommandResult<T>;
  state: CommunicationCommandState;
}

function cloneState(state: CommunicationCommandState): CommunicationCommandState {
  return {
    mailboxes: [...state.mailboxes],
    mailboxCursors: [...state.mailboxCursors],
    threads: [...state.threads],
    messages: [...state.messages],
    deliveries: [...state.deliveries],
    messageAttachments: [...state.messageAttachments],
    messageTemplates: [...state.messageTemplates],
    messageTemplateVersions: [...state.messageTemplateVersions],
    channelSuppressions: [...state.channelSuppressions],
    outboxJobs: [...state.outboxJobs],
    commandReceipts: [...state.commandReceipts],
    auditLog: [...state.auditLog],
    events: [...state.events],
    tasks: [...state.tasks],
    leads: [...state.leads],
    companies: [...state.companies],
    contacts: [...state.contacts],
    partyChannels: [...state.partyChannels],
    memberships: [...state.memberships],
  };
}

function failure<T>(
  state: CommunicationCommandState,
  context: CommandContext,
  code: Parameters<typeof commandError>[1],
  message: string,
): CommunicationExecution<T> {
  const severity = code === "persistence_error" ? "error" : "warning";
  return { state, result: commandError(context, code, message, { severity }) as CommandResult<T> };
}

const INVALID_COMMUNICATION_CONTEXT: CommandContext = Object.freeze({
  ownerId: "invalid-context",
  actorType: "system",
  actorId: "communication-command",
  origin: "system",
  commandId: "invalid-context",
  correlationId: "invalid-context",
  idempotencyKey: "invalid-context",
  requestedAt: "1970-01-01T00:00:00.000Z",
  schemaVersion: 1,
});

function parseCommunicationCommandContext(value: unknown): CommandContext | undefined {
  const normalized = normalizeCommunicationJson(value);
  if (normalized === undefined) return undefined;
  const parsed = commandContextSchema.safeParse(normalized);
  return parsed.success ? parsed.data : undefined;
}

function invalidCommunicationCommandContext<T>(state: CommunicationCommandState): CommunicationExecution<T> {
  return failure(state, INVALID_COMMUNICATION_CONTEXT, "validation_error", "Contexto de comando inválido.");
}

function replayLookup<T>(
  state: CommunicationCommandState,
  context: CommandContext,
  command: string,
  fingerprint: string,
): CommunicationExecution<T> | null {
  const receipt = state.commandReceipts.find(
    (item) =>
      item.ownerId === context.ownerId &&
      item.command === command &&
      item.idempotencyKey === context.idempotencyKey,
  );
  if (!receipt) return null;
  if (receipt.requestFingerprint !== fingerprint)
    return failure<T>(state, context, "idempotency_conflict", "A chave de idempotência já foi usada para outra intenção.");
  if (!receipt.result.ok) return { state, result: receipt.result as CommandResult<T> };
  return { state, result: { ...receipt.result, status: "replayed" } as CommandSuccess<T> };
}

interface CommitInput<T> {
  state: CommunicationCommandState;
  context: CommandContext;
  command: string;
  fingerprint: string;
  targetType: string;
  targetId: string;
  value: T;
  version: number;
  events?: ActivityEvent[];
  outboxJobIds?: string[];
  warnings?: string[];
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  previousVersion?: number;
  deps: CommunicationCommandDeps;
}

function commit<T>(input: CommitInput<T>): CommunicationExecution<T> {
  const occurredAt = input.deps.now();
  const auditId = input.deps.uuid();
  const result: CommandSuccess<T> = {
    ok: true,
    status: "applied",
    value: input.value,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    version: input.version,
    eventIds: (input.events ?? []).map((event) => event.id),
    outboxJobIds: input.outboxJobIds ?? [],
    auditId,
    warnings: input.warnings ?? [],
  };
  const audit: AuditEntry = {
    id: auditId,
    ownerId: input.context.ownerId,
    command: input.command,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    causationId: input.context.causationId,
    actorType: input.context.actorType,
    actorId: input.context.actorId,
    origin: input.context.origin,
    targetType: input.targetType,
    targetId: input.targetId,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    result: "applied" satisfies AuditResult,
    occurredAt,
    expectedVersion: input.context.expectedVersion,
    previousVersion: input.previousVersion,
    nextVersion: input.version,
    before: input.before,
    after: input.after,
  };
  const receipt: CommandReceipt = {
    id: input.deps.uuid(),
    ownerId: input.context.ownerId,
    command: input.command,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    result,
    createdAt: occurredAt,
    schemaVersion: input.context.schemaVersion,
  };
  return {
    result,
    state: {
      ...input.state,
      events: [...input.state.events, ...(input.events ?? [])],
      auditLog: [...input.state.auditLog, audit],
      commandReceipts: [...input.state.commandReceipts, receipt],
    },
  };
}

function preview(text: string): string {
  return text.replace(/\s+/g, " ").trim().slice(0, 180);
}

// As classes usam intervalo acentuado porque `\w` não cobre "ç"/"ã" em JavaScript.
const OPT_OUT_PATTERN =
  /(descadastr[\wÀ-ÿ]*|cancelar\s+(?:a\s+)?(?:minha\s+)?inscri[\wÀ-ÿ]*|remover\s+da\s+lista|unsubscribe|opt[-\s]?out|sair\s+da\s+lista)/i;
const OUT_OF_OFFICE_PATTERN =
  /(f[ée]rias|ausente\s+do\s+escrit[óo]rio|fora\s+do\s+escrit[óo]rio|out\s+of\s+office|retorno\s+em|estarei\s+de\s+volta)/i;
const RETURN_DATE_PATTERN = /\b(\d{2})\/(\d{2})\/(\d{4})\b/;

/**
 * Fingerprint auditável: hash curto de uma projeção sanitizada. Cabeçalhos
 * fora da allowlist e corpo integral nunca chegam ao recibo nem à auditoria.
 */
function sanitizedFingerprint(value: unknown): string {
  return contentHash([stableFingerprint(value)]);
}

/** Detecção determinística de pedido explícito de descadastro. */
export function detectOptOut(
  _headers: Partial<Record<string, string>>,
  bodyText: string,
): boolean {
  return OPT_OUT_PATTERN.test(bodyText);
}

export interface OutOfOfficeDetection {
  detected: boolean;
  returnDate?: string;
}

/** Out-of-office só adia quando a data de retorno é explícita e legível. */
export function detectOutOfOffice(subject: string, bodyText: string): OutOfOfficeDetection {
  const haystack = `${subject}\n${bodyText}`;
  if (!OUT_OF_OFFICE_PATTERN.test(haystack)) return { detected: false };
  const match = haystack.match(RETURN_DATE_PATTERN);
  if (!match) return { detected: true };
  const [, day, month, year] = match;
  const parsed = new Date(`${year}-${month}-${day}T12:00:00.000Z`);
  const valid =
    !Number.isNaN(parsed.getTime()) &&
    parsed.getUTCFullYear() === Number(year) &&
    parsed.getUTCMonth() + 1 === Number(month) &&
    parsed.getUTCDate() === Number(day);
  return !valid
    ? { detected: true }
    : { detected: true, returnDate: parsed.toISOString() };
}

const addressSchema = z.string().trim().min(1).max(320);

export const importMessageSchema = z.object({
  mailboxId: z.string().trim().min(1),
  folder: z.enum(MAILBOX_FOLDERS),
  uid: z.number().int().nonnegative().optional(),
  uidValidity: z.number().int().nonnegative().optional(),
  direction: z.enum(["inbound", "outbound"]),
  externalMessageId: z.string().trim().min(3),
  inReplyTo: z.string().trim().min(3).optional(),
  references: z.array(z.string().trim().min(3)).default([]),
  from: addressSchema,
  to: z.array(addressSchema).default([]),
  cc: z.array(addressSchema).default([]),
  subject: z.string().max(500).default(""),
  bodyText: z.string().max(200_000).default(""),
  occurredAt: z.string().datetime({ offset: true }),
  headers: z.record(z.string(), z.string()).default({}),
  attachments: z
    .array(
      z.object({
        filename: z.string().trim().min(1).max(255),
        declaredMimeType: z.string().trim().min(1).max(255),
        size: z.number().int().nonnegative(),
        contentHash: z.string().trim().min(1).max(128),
      }),
    )
    .default([]),
  originLeadId: z.string().trim().min(1).optional(),
});

export type ImportMessageInput = z.input<typeof importMessageSchema>;

export interface ImportMessageValue {
  message?: CommunicationMessage;
  thread?: CommunicationThread;
  duplicate: boolean;
  duplicateEvidence?: DuplicateEvidence;
  resolution?: ThreadResolution;
  countedAsInteraction: boolean;
  optOut?: ChannelSuppression;
  outOfOffice?: OutOfOfficeAction;
  humanTask?: Task;
}

function nextThread(
  state: CommunicationCommandState,
  resolution: ThreadResolution,
  input: z.output<typeof importMessageSchema>,
  counterpart: string,
  now: string,
  deps: CommunicationCommandDeps,
  ownerId: string,
): CommunicationThread {
  const existing = resolution.threadId
    ? state.threads.find(
        (thread) =>
          thread.id === resolution.threadId &&
          thread.ownerId === ownerId &&
          thread.mailboxId === input.mailboxId,
      )
    : undefined;
  if (existing) return existing;
  return {
    id: deps.uuid(),
    ownerId,
    channel: EMAIL_CHANNEL,
    mailboxId: input.mailboxId,
    companyId: resolution.companyId,
    contactId: resolution.contactId,
    leadId: resolution.leadId,
    funnelId: resolution.funnelId,
    subject: input.subject || "(sem assunto)",
    normalizedSubject: normalizeSubject(input.subject),
    counterpartAddress: counterpart,
    linkState: resolution.linkState,
    linkReason: resolution.linkReason,
    state: "open",
    needsReply: false,
    unread: false,
    lastMessageAt: input.occurredAt,
    version: 1,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Importa uma mensagem já obtida pelo adapter. A função não busca nada: ela
 * apenas decide duplicata, vínculo, interação real e efeitos derivados.
 */
export function executeImportMessage(
  stateInput: CommunicationCommandState,
  input: ImportMessageInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<ImportMessageValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const command = "communication.message.import";
  let parsed: z.output<typeof importMessageSchema>;
  try {
    parsed = importMessageSchema.parse(normalizeCommunicationJson(input));
  } catch (error) {
    const message = error instanceof z.ZodError ? error.issues[0]?.message ?? "Mensagem inválida." : "Mensagem inválida.";
    return failure(stateInput, context, "validation_error", message);
  }

  const fingerprint = sanitizedFingerprint({
    ...parsed,
    headers: allowedHeaders(parsed.headers),
    bodyText: contentHash([parsed.bodyText]),
  });
  const state = cloneState(stateInput);
  const replay = replayLookup<ImportMessageValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const mailbox = state.mailboxes.find((item) => item.id === parsed.mailboxId && item.ownerId === context.ownerId);
  if (!mailbox) return failure(stateInput, context, "not_found", "Caixa de e-mail não encontrada.");

  const from = parseAddress(parsed.from);
  const primaryRecipient = parseAddress(parsed.to[0]);
  const counterpartRaw = parsed.direction === "inbound" ? from : primaryRecipient;
  const counterpart = counterpartRaw?.normalized ?? null;
  const headers = allowedHeaders(parsed.headers);
  const externalMessageId = normalizeMessageId(parsed.externalMessageId);
  if (!externalMessageId)
    return failure(stateInput, context, "validation_error", "Message-ID inválido.");
  const inReplyTo = normalizeMessageId(parsed.inReplyTo) ?? undefined;
  const references = [
    ...new Set(parsed.references.flatMap((reference) => parseReferences(reference))),
  ];
  const hash = contentHash([
    parsed.direction,
    parsed.subject,
    parsed.bodyText,
    parsed.occurredAt,
    from?.normalized ?? parsed.from.trim().toLowerCase(),
    parsed.to.map((address) => parseAddress(address)?.normalized ?? address.trim().toLowerCase()).join(","),
  ]);

  const duplicate = findDuplicate(state.messages.filter((item) => item.ownerId === context.ownerId), {
    mailboxId: parsed.mailboxId,
    folder: parsed.folder,
    uid: parsed.uid,
    uidValidity: parsed.uidValidity,
    externalMessageId,
    contentHash: hash,
  });
  if (duplicate.duplicate) {
    return commit<ImportMessageValue>({
      state,
      context,
      command,
      fingerprint,
      targetType: "communication_message",
      targetId: duplicate.existing?.id ?? parsed.externalMessageId,
      value: { duplicate: true, duplicateEvidence: duplicate.evidence, countedAsInteraction: false, message: duplicate.existing },
      version: duplicate.existing?.version ?? 1,
      warnings: ["Mensagem já importada; nenhuma alteração foi aplicada."],
      deps,
    });
  }

  const resolution = resolveThreadLink(
    {
      mailboxId: parsed.mailboxId,
      ownerId: context.ownerId,
      direction: parsed.direction,
      externalMessageId,
      inReplyTo,
      references,
      counterpartAddress: counterpartRaw?.address ?? "",
      subject: parsed.subject,
      originLeadId: parsed.originLeadId,
    },
    state,
  );

  const now = deps.now();
  const thread = nextThread(state, resolution, parsed, counterpart ?? "", now, deps, context.ownerId);
  const isNewThread = !state.threads.some((item) => item.id === thread.id);

  const message: CommunicationMessage = {
    id: deps.uuid(),
    ownerId: context.ownerId,
    threadId: thread.id,
    mailboxId: parsed.mailboxId,
    channel: EMAIL_CHANNEL,
    direction: parsed.direction,
    folder: parsed.folder,
    uid: parsed.uid,
    uidValidity: parsed.uidValidity,
    externalMessageId,
    inReplyTo,
    references,
    fromAddress: from?.address ?? parsed.from,
    fromName: from?.name,
    toAddresses: parsed.to,
    ccAddresses: parsed.cc,
    subject: parsed.subject,
    bodyText: parsed.bodyText,
    bodyPreview: preview(parsed.bodyText),
    headers,
    state: parsed.direction === "inbound" ? "received" : "sent",
    occurredAt: parsed.occurredAt,
    importedAt: now,
    contentHash: hash,
    hasAttachments: parsed.attachments.length > 0,
    version: 1,
    createdAt: now,
    updatedAt: now,
  };

  const attachments: MessageAttachmentMeta[] = parsed.attachments.map((attachment) => ({
    id: deps.uuid(),
    ownerId: context.ownerId,
    messageId: message.id,
    filename: attachment.filename.replace(/[\\/]/g, "_"),
    declaredMimeType: attachment.declaredMimeType,
    size: attachment.size,
    contentHash: attachment.contentHash,
    scanState: "pending",
    createdAt: now,
    updatedAt: now,
  }));

  const outOfOfficeDetection = parsed.direction === "inbound"
    ? detectOutOfOffice(parsed.subject, parsed.bodyText)
    : { detected: false };
  const autoReply = parsed.direction === "inbound" &&
    (isAutoResponder(headers) || outOfOfficeDetection.detected);
  const linked = resolution.linkState === "linked" && Boolean(resolution.leadId);
  const events: ActivityEvent[] = [];
  let countedAsInteraction = false;

  if (parsed.direction === "inbound" && linked) {
    countedAsInteraction = !autoReply;
    events.push({
      id: deps.uuid(),
      leadId: resolution.leadId as string,
      type: autoReply ? "email_auto_reply_received" : "email_received",
      title: autoReply ? "Resposta automática recebida" : "E-mail recebido",
      description: message.subject || undefined,
      occurredAt: parsed.occurredAt,
      kind: "event",
      actorType: context.actorType,
      actorId: context.actorId,
      origin: context.origin,
      commandId: context.commandId,
      correlationId: context.correlationId,
      causationId: context.causationId,
      countsAsInteraction: countedAsInteraction,
    });
  }

  // Somente resposta humana recebida atualiza a última interação real.
  const leads = countedAsInteraction
    ? state.leads.map((lead) =>
        lead.id === resolution.leadId &&
        (!lead.lastInteractionAt || lead.lastInteractionAt < parsed.occurredAt)
          ? { ...lead, lastInteractionAt: parsed.occurredAt }
          : lead,
      )
    : state.leads;

  let suppressions = state.channelSuppressions;
  let optOut: ChannelSuppression | undefined;
  if (parsed.direction === "inbound" && counterpart && detectOptOut(headers, parsed.bodyText)) {
    const already = findSuppression(
      suppressions.filter((item) => item.ownerId === context.ownerId),
      { normalizedAddress: counterpart },
    );
    if (!already) {
      optOut = {
        id: deps.uuid(),
        ownerId: context.ownerId,
        channel: EMAIL_CHANNEL,
        normalizedAddress: counterpart,
        scope: "global",
        reason: "unsubscribe",
        evidenceMessageId: message.id,
        createdAt: now,
      };
      suppressions = [...suppressions, optOut];
    } else {
      optOut = already;
    }
  }

  let outOfOffice: OutOfOfficeAction | undefined;
  if (parsed.direction === "inbound" && !optOut && outOfOfficeDetection.detected)
    outOfOffice = outOfOfficeDecision(outOfOfficeDetection.returnDate, parsed.occurredAt);

  const inbound = parsed.direction === "inbound";
  const deferred = outOfOffice?.action === "defer" ? outOfOffice.until : undefined;
  const updatedThread: CommunicationThread = {
    ...thread,
    companyId: thread.companyId ?? resolution.companyId,
    contactId: thread.contactId ?? resolution.contactId,
    leadId: thread.leadId ?? resolution.leadId,
    funnelId: thread.funnelId ?? resolution.funnelId,
    counterpartAddress: thread.counterpartAddress || (counterpart ?? ""),
    lastMessageAt:
      parsed.occurredAt > thread.lastMessageAt || isNewThread ? parsed.occurredAt : thread.lastMessageAt,
    lastInboundAt: inbound ? parsed.occurredAt : thread.lastInboundAt,
    lastOutboundAt: inbound ? thread.lastOutboundAt : parsed.occurredAt,
    unread: inbound ? true : thread.unread,
    needsReply: inbound ? !autoReply && !optOut : false,
    state: deferred ? "snoozed" : inbound ? "open" : thread.state,
    snoozedUntil: deferred ?? (inbound ? undefined : thread.snoozedUntil),
    version: isNewThread ? thread.version : thread.version + 1,
    updatedAt: now,
  };

  const cursors = state.mailboxCursors.map((cursor) =>
    cursor.mailboxId === parsed.mailboxId && cursor.folder === parsed.folder && parsed.uid !== undefined
      ? {
          ...cursor,
          uidNext: Math.max(cursor.uidNext, parsed.uid + 1),
          uidValidity: parsed.uidValidity ?? cursor.uidValidity,
          lastSyncedAt: now,
          syncState: "synced" as const,
          updatedAt: now,
        }
      : cursor,
  );

  let nextState: CommunicationCommandState = {
    ...state,
    threads: isNewThread
      ? [...state.threads, updatedThread]
      : state.threads.map((item) => (item.id === updatedThread.id ? updatedThread : item)),
    messages: [...state.messages, message],
    messageAttachments: [...state.messageAttachments, ...attachments],
    channelSuppressions: suppressions,
    mailboxCursors: cursors,
    leads,
  };

  let humanTask: Task | undefined;
  if (outOfOffice?.action === "human_task" && linked && resolution.leadId) {
    const membershipId = state.memberships.find(
      (membership) =>
        membership.leadId === resolution.leadId &&
        (!resolution.funnelId || membership.funnelId === resolution.funnelId),
    )?.id;
    const taskContext: CommandContext = {
      ...context,
      commandId: deps.uuid(),
      causationId: context.commandId,
      idempotencyKey: `${context.idempotencyKey}:out-of-office-task`,
      requestedAt: now,
      expectedVersion: undefined,
    };
    const taskExecution = executeCreateTask(
      {
        tasks: nextState.tasks,
        events: nextState.events,
        commandReceipts: nextState.commandReceipts,
        auditLog: nextState.auditLog,
        outboxJobs: nextState.outboxJobs,
        leadIds: nextState.leads.map((lead) => lead.id),
        membershipIds: nextState.memberships.map((membership) => membership.id),
      },
      {
        leadId: resolution.leadId,
        membershipId,
        description: "Revisar resposta de ausência sem data de retorno",
        dueAt: now,
        priority: "medium",
        note: `A conversa "${updatedThread.subject}" recebeu uma resposta automática sem data confiável. Defina quando retomar o contato.`,
        origin: "automation",
        actionType: "human_task",
        sensitive: false,
        immutableReference: {
          type: "communication_message",
          id: message.id,
          version: message.version,
          blocking: true,
        },
      },
      taskContext,
      deps,
    );
    if (!taskExecution.result.ok)
      return failure(
        stateInput,
        context,
        taskExecution.result.error.code,
        `Não foi possível criar a tarefa da resposta de ausência: ${taskExecution.result.error.message}`,
      );
    humanTask = taskExecution.result.value.task;
    nextState = {
      ...nextState,
      tasks: taskExecution.state.tasks,
      events: taskExecution.state.events,
      commandReceipts: taskExecution.state.commandReceipts,
      auditLog: taskExecution.state.auditLog,
      outboxJobs: taskExecution.state.outboxJobs,
    };
  }

  const warnings: string[] = [];
  if (!linked) warnings.push("Mensagem enviada para a fila Não vinculadas; nenhum Lead foi criado.");
  if (optOut) warnings.push("Descadastro explícito registrado como supressão global.");
  if (humanTask)
    warnings.push("Resposta automática de ausência sem data confiável: tarefa humana criada.");
  else if (outOfOffice?.action === "human_task")
    warnings.push("Resposta automática de ausência sem data confiável: uma pessoa precisa decidir.");

  return commit<ImportMessageValue>({
    state: nextState,
    context,
    command,
    fingerprint,
    targetType: "communication_message",
    targetId: message.id,
    value: {
      message,
      thread: updatedThread,
      duplicate: false,
      resolution,
      countedAsInteraction,
      optOut,
      outOfOffice,
      humanTask,
    },
    version: message.version,
    events,
    warnings,
    after: { threadId: updatedThread.id, linkState: updatedThread.linkState, state: message.state },
    deps,
  });
}

export const saveDraftSchema = z.object({
  draftId: z.string().trim().min(1).optional(),
  threadId: z.string().trim().min(1).optional(),
  mailboxId: z.string().trim().min(1),
  leadId: z.string().trim().min(1).optional(),
  to: z.array(addressSchema).length(1, "A v1 aceita exatamente um destinatário por conversa"),
  subject: z.string().trim().max(500).default(""),
  bodyText: z.string().trim().min(1, "Escreva a mensagem").max(200_000),
  templateVersionId: z.string().trim().min(1).optional(),
});

export type SaveDraftInput = z.input<typeof saveDraftSchema>;

export interface SaveDraftValue {
  message: CommunicationMessage;
  thread: CommunicationThread;
}

export function executeSaveDraft(
  stateInput: CommunicationCommandState,
  input: SaveDraftInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<SaveDraftValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const command = "communication.draft.save";
  let parsed: z.output<typeof saveDraftSchema>;
  try {
    parsed = saveDraftSchema.parse(normalizeCommunicationJson(input));
  } catch (error) {
    const message = error instanceof z.ZodError ? error.issues[0]?.message ?? "Rascunho inválido." : "Rascunho inválido.";
    return failure(stateInput, context, "validation_error", message);
  }

  const fingerprint = sanitizedFingerprint({ ...parsed, bodyText: contentHash([parsed.bodyText]) });
  const state = cloneState(stateInput);
  const replay = replayLookup<SaveDraftValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const mailbox = state.mailboxes.find((item) => item.id === parsed.mailboxId && item.ownerId === context.ownerId);
  if (!mailbox) return failure(stateInput, context, "not_found", "Caixa de e-mail não encontrada.");

  const recipient = parseAddress(parsed.to[0]);
  if (!recipient?.normalized) return failure(stateInput, context, "validation_error", "Destinatário inválido.");

  const now = deps.now();
  const existingDraft = parsed.draftId
    ? state.messages.find((item) => item.id === parsed.draftId && item.ownerId === context.ownerId)
    : undefined;
  if (parsed.draftId && !existingDraft) return failure(stateInput, context, "not_found", "Rascunho não encontrado.");
  if (existingDraft && existingDraft.state !== "draft")
    return failure(stateInput, context, "invalid_state", "Somente rascunhos podem ser editados.");
  if (existingDraft && existingDraft.mailboxId !== parsed.mailboxId)
    return failure(stateInput, context, "validation_error", "O rascunho pertence a outra caixa de e-mail.");
  if (existingDraft && context.expectedVersion !== undefined && context.expectedVersion !== existingDraft.version)
    return failure(stateInput, context, "version_conflict", "O rascunho foi alterado por outra operação.");

  let thread = parsed.threadId
    ? state.threads.find(
        (item) =>
          item.id === parsed.threadId &&
          item.ownerId === context.ownerId &&
          item.mailboxId === parsed.mailboxId,
      )
    : existingDraft
      ? state.threads.find(
          (item) =>
            item.id === existingDraft.threadId &&
            item.ownerId === context.ownerId &&
            item.mailboxId === parsed.mailboxId,
        )
      : undefined;
  if (parsed.threadId && !thread) return failure(stateInput, context, "not_found", "Conversa não encontrada.");
  if (existingDraft && parsed.threadId && existingDraft.threadId !== parsed.threadId)
    return failure(stateInput, context, "validation_error", "O rascunho pertence a outra conversa.");

  if (parsed.templateVersionId) {
    const templateVersion = state.messageTemplateVersions.find(
      (item) => item.id === parsed.templateVersionId && item.ownerId === context.ownerId,
    );
    const template = templateVersion
      ? state.messageTemplates.find(
          (item) =>
            item.id === templateVersion.templateId &&
            item.ownerId === context.ownerId &&
            item.channel === EMAIL_CHANNEL &&
            !item.archived,
        )
      : undefined;
    if (!templateVersion || !template)
      return failure(stateInput, context, "not_found", "Versão de template de e-mail não encontrada.");
  }

  if (thread?.counterpartAddress && thread.counterpartAddress !== recipient.normalized)
    return failure(stateInput, context, "validation_error", "O destinatário deve ser o endpoint fixado da conversa.");

  let isNewThread = false;
  if (!thread) {
    const resolution = resolveThreadLink(
      {
        ownerId: context.ownerId,
        mailboxId: parsed.mailboxId,
        direction: "outbound",
        externalMessageId: `draft-${parsed.draftId ?? "novo"}`,
        references: [],
        counterpartAddress: recipient.address,
        subject: parsed.subject,
        originLeadId: parsed.leadId,
      },
      state,
    );
    isNewThread = true;
    thread = {
      id: deps.uuid(),
      ownerId: context.ownerId,
      channel: EMAIL_CHANNEL,
      mailboxId: parsed.mailboxId,
      companyId: resolution.companyId,
      contactId: resolution.contactId,
      leadId: resolution.leadId,
      funnelId: resolution.funnelId,
      subject: parsed.subject || "(sem assunto)",
      normalizedSubject: normalizeSubject(parsed.subject),
      counterpartAddress: recipient.normalized,
      linkState: resolution.linkState,
      linkReason: resolution.linkReason,
      state: "open",
      needsReply: false,
      unread: false,
      lastMessageAt: now,
      version: 1,
      createdAt: now,
      updatedAt: now,
    };
  }

  const previousVersion = existingDraft?.version ?? 0;
  const message: CommunicationMessage = existingDraft
    ? {
        ...existingDraft,
        toAddresses: parsed.to,
        subject: parsed.subject,
        bodyText: parsed.bodyText,
        bodyPreview: preview(parsed.bodyText),
        templateVersionId: parsed.templateVersionId,
        version: existingDraft.version + 1,
        updatedAt: now,
      }
    : {
        id: deps.uuid(),
        ownerId: context.ownerId,
        threadId: thread.id,
        mailboxId: parsed.mailboxId,
        channel: EMAIL_CHANNEL,
        direction: "outbound",
        folder: "sent",
        externalMessageId: `cosmos-${deps.uuid()}@${mailbox.normalizedAddress.split("@")[1] ?? "local"}`,
        references: [],
        fromAddress: mailbox.address,
        fromName: mailbox.displayName,
        toAddresses: parsed.to,
        ccAddresses: [],
        subject: parsed.subject,
        bodyText: parsed.bodyText,
        bodyPreview: preview(parsed.bodyText),
        headers: {},
        state: "draft",
        occurredAt: now,
        importedAt: now,
        contentHash: contentHash([parsed.subject, parsed.bodyText, now]),
        hasAttachments: false,
        templateVersionId: parsed.templateVersionId,
        commandId: context.commandId,
        correlationId: context.correlationId,
        version: 1,
        createdAt: now,
        updatedAt: now,
      };

  const updatedThread: CommunicationThread = {
    ...thread,
    lastMessageAt: thread.lastMessageAt > now ? thread.lastMessageAt : now,
    version: isNewThread ? thread.version : thread.version + 1,
    updatedAt: now,
  };

  const nextState: CommunicationCommandState = {
    ...state,
    threads: isNewThread
      ? [...state.threads, updatedThread]
      : state.threads.map((item) => (item.id === updatedThread.id ? updatedThread : item)),
    messages: existingDraft
      ? state.messages.map((item) => (item.id === message.id ? message : item))
      : [...state.messages, message],
  };

  return commit<SaveDraftValue>({
    state: nextState,
    context,
    command,
    fingerprint,
    targetType: "communication_message",
    targetId: message.id,
    value: { message, thread: updatedThread },
    version: message.version,
    previousVersion,
    before: existingDraft ? { subject: existingDraft.subject, version: existingDraft.version } : undefined,
    after: { subject: message.subject, version: message.version },
    deps,
  });
}

export const queueSendSchema = z.object({
  draftId: z.string().trim().min(1),
  sentInRun: z.number().int().nonnegative().default(0),
});

const deliveryModeSchema = z.enum(DELIVERY_MODES);

export type QueueSendInput = z.input<typeof queueSendSchema>;

export interface QueueSendValue {
  message: CommunicationMessage;
  delivery: MessageDelivery;
  outboxJob?: OutboxJob;
  queued: boolean;
  blockReason?: SendBlockReason;
  scheduledAt?: string;
  suppression?: ChannelSuppression;
}

function deliveryOutcomes(state: CommunicationCommandState, mailboxId: string): DeliveryOutcome[] {
  return state.deliveries
    .filter((delivery) => delivery.mailboxId === mailboxId)
    .flatMap((delivery) =>
      delivery.state === "sent" ||
      delivery.state === "delivered" ||
      delivery.state === "deferred" ||
      delivery.state === "bounced" ||
      delivery.state === "rejected" ||
      delivery.state === "complained" ||
      delivery.state === "append_pending"
        ? [{ kind: delivery.state, bounceType: delivery.bounceType, occurredAt: delivery.occurredAt }]
        : [],
    );
}

export function mailboxHealthFromState(state: CommunicationCommandState, mailboxId: string): MailboxHealth {
  return evaluateMailboxHealth(deliveryOutcomes(state, mailboxId));
}

/**
 * Enfileira um envio **simulado**: cria a intenção persistida, a tentativa e o
 * job de outbox. Nenhum socket é aberto e nada é entregue.
 */
export function executeQueueSend(
  stateInput: CommunicationCommandState,
  input: QueueSendInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
  deliveryMode: DeliveryMode = "simulated",
): CommunicationExecution<QueueSendValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const command = "communication.send.queue";
  let parsed: z.output<typeof queueSendSchema>;
  let parsedDeliveryMode: DeliveryMode;
  try {
    parsed = queueSendSchema.parse(normalizeCommunicationJson(input));
    parsedDeliveryMode = deliveryModeSchema.parse(normalizeCommunicationJson(deliveryMode));
  } catch (error) {
    const message = error instanceof z.ZodError ? error.issues[0]?.message ?? "Envio inválido." : "Envio inválido.";
    return failure(stateInput, context, "validation_error", message);
  }

  const fingerprint = sanitizedFingerprint({ ...parsed, deliveryMode: parsedDeliveryMode });
  const state = cloneState(stateInput);
  const replay = replayLookup<QueueSendValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const draft = state.messages.find((item) => item.id === parsed.draftId && item.ownerId === context.ownerId);
  if (!draft) return failure(stateInput, context, "not_found", "Rascunho não encontrado.");
  if (draft.state !== "draft") return failure(stateInput, context, "invalid_state", "Somente rascunhos podem ser enfileirados.");
  if (context.expectedVersion !== undefined && context.expectedVersion !== draft.version)
    return failure(stateInput, context, "version_conflict", "O rascunho foi alterado por outra operação.");

  const mailbox = state.mailboxes.find(
    (item) => item.id === draft.mailboxId && item.ownerId === context.ownerId,
  );
  if (!mailbox) return failure(stateInput, context, "not_found", "Caixa de e-mail não encontrada.");
  const thread = state.threads.find(
    (item) =>
      item.id === draft.threadId &&
      item.ownerId === context.ownerId &&
      item.mailboxId === mailbox.id,
  );
  if (!thread) return failure(stateInput, context, "not_found", "Conversa não encontrada.");
  if (thread.state !== "open")
    return failure(stateInput, context, "invalid_state", "Reabra a conversa antes de enfileirar um envio.");
  if (thread.linkState === "blocked" || thread.linkState === "not_commercial")
    return failure(stateInput, context, "invalid_state", "A classificação da conversa bloqueia novos envios.");

  if (draft.templateVersionId) {
    const templateVersion = state.messageTemplateVersions.find(
      (item) =>
        item.id === draft.templateVersionId &&
        item.ownerId === context.ownerId &&
        item.status === "published",
    );
    const template = templateVersion
      ? state.messageTemplates.find(
          (item) =>
            item.id === templateVersion.templateId &&
            item.ownerId === context.ownerId &&
            item.channel === EMAIL_CHANNEL &&
            !item.archived,
        )
      : undefined;
    if (!templateVersion || !template)
      return failure(
        stateInput,
        context,
        "invalid_state",
        "O envio exige uma versão publicada e ativa do template selecionado.",
      );
  }

  const recipient = normalizeEmail(parseAddress(draft.toAddresses[0])?.address ?? "");
  if (!recipient) return failure(stateInput, context, "validation_error", "Destinatário inválido.");

  const now = deps.now();
  const health = mailboxHealthFromState(state, mailbox.id);
  const dayKey = localDayKey(now);
  const dayAgo = new Date(new Date(now).getTime() - 24 * 60 * 60 * 1000).toISOString();
  const sentInLastDay = state.deliveries.filter(
    (delivery) =>
      delivery.ownerId === context.ownerId &&
      delivery.mailboxId === mailbox.id &&
      delivery.occurredAt >= dayAgo &&
      delivery.state !== "suppressed" &&
      delivery.state !== "cancelled",
  ).length;
  const sentInLocalDay = state.deliveries.filter(
    (delivery) =>
      delivery.ownerId === context.ownerId &&
      delivery.mailboxId === mailbox.id &&
      localDayKey(delivery.occurredAt) === dayKey &&
      delivery.state !== "suppressed" &&
      delivery.state !== "cancelled",
  ).length;

  const decision = evaluateSendPolicy({
    now,
    mailboxActive: mailbox.active,
    normalizedAddress: recipient,
    funnelId: thread.funnelId,
    suppressions: state.channelSuppressions.filter((item) => item.ownerId === context.ownerId),
    health,
    warmupStartedAt: mailbox.warmupStartedAt,
    sentInLastDay,
    sentInLocalDay,
    sentInRun: parsed.sentInRun,
  });

  const deferrable = decision.reason === "outside_window" || decision.reason === "daily_cap" || decision.reason === "warmup_cap";
  const queued = decision.allowed || deferrable;
  const scheduledAt = decision.allowed ? now : decision.retryAt;

  const deliveryState = decision.reason === "suppressed" ? "suppressed" : queued ? "queued" : "cancelled";
  const delivery: MessageDelivery = {
    id: deps.uuid(),
    ownerId: context.ownerId,
    messageId: draft.id,
    mailboxId: mailbox.id,
    attempt: 0,
    state: deliveryState,
    deliveryMode: parsedDeliveryMode,
    idempotencyKey: `${command}:${draft.id}`,
    appendState: "not_required",
    suppressionId: decision.suppression?.id,
    occurredAt: now,
    createdAt: now,
    updatedAt: now,
  };

  const outboxJob: OutboxJob | undefined = queued
    ? {
        id: deps.uuid(),
        ownerId: context.ownerId,
        kind: "email.send",
        payloadVersion: 1,
        payload: {
          provider: EMAIL_CHANNEL,
          deliveryMode: parsedDeliveryMode,
          messageId: draft.id,
          deliveryId: delivery.id,
          mailboxId: mailbox.id,
          threadId: thread.id,
        },
        idempotencyKey: delivery.idempotencyKey,
        commandId: context.commandId,
        correlationId: context.correlationId,
        causationId: context.causationId,
        status: "pending",
        scheduledAt: scheduledAt ?? now,
        attempts: 0,
        maxAttempts: 3,
        createdAt: now,
        updatedAt: now,
      }
    : undefined;

  const messageState: MessageState = decision.reason === "suppressed" ? "suppressed" : queued ? "queued" : "draft";
  const message: CommunicationMessage = {
    ...draft,
    state: messageState,
    version: draft.version + 1,
    updatedAt: now,
  };

  const warnings: string[] = [];
  if (decision.reason === "suppressed")
    warnings.push("Destinatário suprimido: o envio foi bloqueado e não entra na fila.");
  else if (deferrable) warnings.push("Fora da janela ou do limite atual: o envio ficou agendado, sem rajada.");
  else if (!decision.allowed) warnings.push("A caixa não pode enfileirar envios agora.");
  if (parsedDeliveryMode === "simulated")
    warnings.push("Envio simulado: nenhuma mensagem sai do CosmosLeads nesta etapa.");

  const nextState: CommunicationCommandState = {
    ...state,
    messages: state.messages.map((item) => (item.id === message.id ? message : item)),
    deliveries: [...state.deliveries, delivery],
    outboxJobs: outboxJob ? [...state.outboxJobs, outboxJob] : state.outboxJobs,
  };

  return commit<QueueSendValue>({
    state: nextState,
    context,
    command,
    fingerprint,
    targetType: "communication_message",
    targetId: message.id,
    value: {
      message,
      delivery,
      outboxJob,
      queued,
      blockReason: decision.reason,
      scheduledAt,
      suppression: decision.suppression ?? undefined,
    },
    version: message.version,
    previousVersion: draft.version,
    outboxJobIds: outboxJob ? [outboxJob.id] : [],
    warnings,
    before: { state: draft.state, version: draft.version },
    after: { state: message.state, version: message.version },
    deps,
  });
}

export const reconcileDeliverySchema = z
  .object({
    deliveryId: z.string().trim().min(1),
    attempt: z.number().int().positive(),
    outcome: z.enum([
      "sent",
      "delivered",
      "deferred",
      "bounced",
      "rejected",
      "complained",
      "ambiguous",
      "cancelled",
      "append_succeeded",
    ]),
    appendFailed: z.boolean().default(false),
    bounceType: z.enum(["transient", "permanent"]).optional(),
    smtpCode: z.string().trim().max(16).optional(),
    providerRequestId: z.string().trim().max(120).optional(),
    sanitizedResponse: z.string().trim().max(400).optional(),
    occurredAt: z.string().datetime({ offset: true }),
  })
  .superRefine((value, refinement) => {
    if (value.outcome === "bounced" && !value.bounceType)
      refinement.addIssue({ code: "custom", path: ["bounceType"], message: "Bounce deve declarar o tipo." });
    if (value.outcome !== "bounced" && value.bounceType)
      refinement.addIssue({ code: "custom", path: ["bounceType"], message: "Somente bounce pode declarar tipo." });
    if (value.appendFailed && value.outcome !== "sent")
      refinement.addIssue({ code: "custom", path: ["appendFailed"], message: "Falha de append exige aceite SMTP." });
  });

export type ReconcileDeliveryInput = z.input<typeof reconcileDeliverySchema>;

export interface ReconcileDeliveryValue {
  delivery: MessageDelivery;
  message: CommunicationMessage;
  requiresReconciliation: boolean;
  suppression?: ChannelSuppression;
}

/**
 * Aplica o resultado de uma tentativa. Resultado ambíguo NÃO vira sucesso e
 * uma resposta antiga nunca sobrescreve uma tentativa mais nova.
 */
export function executeReconcileDelivery(
  stateInput: CommunicationCommandState,
  input: ReconcileDeliveryInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<ReconcileDeliveryValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const command = "communication.delivery.reconcile";
  let parsed: z.output<typeof reconcileDeliverySchema>;
  try {
    parsed = reconcileDeliverySchema.parse(normalizeCommunicationJson(input));
  } catch (error) {
    const message = error instanceof z.ZodError ? error.issues[0]?.message ?? "Reconciliação inválida." : "Reconciliação inválida.";
    return failure(stateInput, context, "validation_error", message);
  }

  const fingerprint = sanitizedFingerprint(parsed);
  const state = cloneState(stateInput);
  const replay = replayLookup<ReconcileDeliveryValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const current = state.deliveries.find((item) => item.id === parsed.deliveryId && item.ownerId === context.ownerId);
  if (!current) return failure(stateInput, context, "not_found", "Tentativa de envio não encontrada.");
  if (parsed.attempt < current.attempt)
    return failure(stateInput, context, "invalid_state", "Resposta obsoleta: uma tentativa mais recente já foi registrada.");
  const confirmsPendingAppend =
    parsed.outcome === "append_succeeded" &&
    parsed.attempt === current.attempt &&
    current.state === "append_pending";
  const activeAttempt = current.state === "queued" || current.state === "submitting";
  const retryAfterDeferral = current.state === "deferred" && parsed.attempt > current.attempt;
  const acceptedFollowUp =
    parsed.attempt === current.attempt &&
    ((current.state === "sent" && ["delivered", "bounced", "complained"].includes(parsed.outcome)) ||
      (current.state === "append_pending" &&
        ["append_succeeded", "delivered", "bounced", "complained"].includes(parsed.outcome)) ||
      (current.state === "delivered" && parsed.outcome === "complained"));
  if (!activeAttempt && !retryAfterDeferral && !acceptedFollowUp)
    return failure(stateInput, context, "invalid_state", "A tentativa já possui um resultado incompatível.");

  if (parsed.outcome === "append_succeeded" && !confirmsPendingAppend)
    return failure(stateInput, context, "invalid_state", "Não existe append pendente para confirmar.");

  const draft = state.messages.find(
    (item) =>
      item.id === current.messageId &&
      item.ownerId === context.ownerId &&
      item.mailboxId === current.mailboxId,
  );
  if (!draft) return failure(stateInput, context, "not_found", "Mensagem não encontrada.");
  const thread = state.threads.find(
    (item) =>
      item.id === draft.threadId &&
      item.ownerId === context.ownerId &&
      item.mailboxId === current.mailboxId,
  );
  if (!thread) return failure(stateInput, context, "not_found", "Conversa da mensagem não encontrada.");
  if (TERMINAL_MESSAGE_STATES.includes(draft.state) && parsed.outcome !== "complained")
    return failure(stateInput, context, "invalid_state", "A mensagem já está em um estado terminal.");

  const now = deps.now();
  const ambiguous = parsed.outcome === "ambiguous";
  let nextState: MessageState;
  if (parsed.outcome === "ambiguous") nextState = draft.state;
  else if (parsed.outcome === "sent" && parsed.appendFailed) nextState = "append_pending";
  else if (parsed.outcome === "append_succeeded") nextState = "sent";
  else nextState = parsed.outcome;

  const recipient = normalizeEmail(parseAddress(draft.toAddresses[0])?.address ?? "");
  let suppressions = state.channelSuppressions;
  let suppression: ChannelSuppression | undefined;
  const suppressionReason: SuppressionReason | undefined =
    parsed.outcome === "complained"
      ? "complaint"
      : parsed.outcome === "bounced" && parsed.bounceType === "permanent"
        ? "hard_bounce"
        : undefined;
  if (recipient && suppressionReason) {
    const existing = findSuppression(
      suppressions.filter((item) => item.ownerId === context.ownerId),
      { normalizedAddress: recipient },
    );
    if (existing) suppression = existing;
    else {
      suppression = {
        id: deps.uuid(),
        ownerId: context.ownerId,
        channel: EMAIL_CHANNEL,
        normalizedAddress: recipient,
        scope: suppressionScopeFor(suppressionReason),
        reason: suppressionReason,
        evidenceMessageId: draft.id,
        createdAt: now,
      };
      suppressions = [...suppressions, suppression];
    }
  }

  const delivery: MessageDelivery = {
    ...current,
    attempt: parsed.attempt,
    state: ambiguous ? "submitting" : (nextState as MessageDelivery["state"]),
    appendState:
      parsed.outcome === "sent"
        ? parsed.appendFailed
          ? "failed"
          : "appended"
        : parsed.outcome === "append_succeeded"
          ? "appended"
          : current.appendState,
    bounceType: parsed.bounceType ?? current.bounceType,
    smtpCode: parsed.smtpCode ?? current.smtpCode,
    providerRequestId: parsed.providerRequestId ?? current.providerRequestId,
    sanitizedResponse: parsed.sanitizedResponse ?? current.sanitizedResponse,
    errorCode: ambiguous ? "ambiguous_external_result" : current.errorCode,
    suppressionId: suppression?.id ?? current.suppressionId,
    occurredAt: parsed.occurredAt,
    updatedAt: now,
  };

  const message: CommunicationMessage = {
    ...draft,
    state: nextState,
    version: draft.version + 1,
    updatedAt: now,
  };

  const liveSendConfirmed = current.deliveryMode === "live" && parsed.outcome === "sent";
  const events: ActivityEvent[] =
    liveSendConfirmed && thread.leadId
      ? [
          {
            id: deps.uuid(),
            leadId: thread.leadId,
            type: "email_sent",
            title: "E-mail enviado",
            description: message.subject || undefined,
            occurredAt: parsed.occurredAt,
            kind: "event",
            actorType: context.actorType,
            actorId: context.actorId,
            origin: context.origin,
            commandId: context.commandId,
            correlationId: context.correlationId,
            causationId: context.causationId,
            countsAsInteraction: true,
          },
        ]
      : [];
  const leads = events.length
    ? state.leads.map((lead) =>
        lead.id === thread.leadId &&
        (!lead.lastInteractionAt || lead.lastInteractionAt < parsed.occurredAt)
          ? { ...lead, lastInteractionAt: parsed.occurredAt }
          : lead,
      )
    : state.leads;

  const warnings: string[] = [];
  if (ambiguous)
    warnings.push("Resultado ambíguo: reconciliar por Message-ID e pasta Enviados antes de qualquer nova tentativa.");
  if (parsed.outcome === "sent" && parsed.appendFailed)
    warnings.push("SMTP aceitou e a cópia em Enviados falhou: não reenviar; o estado é append_pending.");
  if (parsed.outcome === "append_succeeded")
    warnings.push("Cópia em Enviados reconciliada sem produzir um segundo envio SMTP.");
  if (suppression) warnings.push("Supressão global registrada para o destinatário.");
  if (current.deliveryMode === "simulated")
    warnings.push("Tentativa simulada: não conta como interação real nem como entrega.");

  return commit<ReconcileDeliveryValue>({
    state: {
      ...state,
      deliveries: state.deliveries.map((item) => (item.id === delivery.id ? delivery : item)),
      messages: state.messages.map((item) => (item.id === message.id ? message : item)),
      channelSuppressions: suppressions,
      leads,
      outboxJobs: state.outboxJobs.map((job) =>
        job.idempotencyKey === delivery.idempotencyKey && job.kind === "email.send"
          ? {
              ...job,
              status: ambiguous ? "reconciling" : parsed.outcome === "deferred" ? "retry_scheduled" : "succeeded",
              attempts: parsed.attempt,
              lastAttemptAt: parsed.occurredAt,
              updatedAt: now,
            }
          : job,
      ),
    },
    context,
    command,
    fingerprint,
    targetType: "message_delivery",
    targetId: delivery.id,
    value: { delivery, message, requiresReconciliation: ambiguous, suppression },
    version: message.version,
    previousVersion: draft.version,
    events,
    warnings,
    before: { state: draft.state, attempt: current.attempt },
    after: { state: message.state, attempt: delivery.attempt },
    deps,
  });
}

export const linkThreadSchema = z.object({
  threadId: z.string().trim().min(1),
  decision: z.enum(["attach_lead", "not_commercial", "block_sender", "pending"]),
  leadId: z.string().trim().min(1).optional(),
});

export type LinkThreadInput = z.input<typeof linkThreadSchema>;

export interface LinkThreadValue {
  thread: CommunicationThread;
  suppression?: ChannelSuppression;
}

/**
 * Decisão humana de vínculo. Nunca cria Lead: anexar exige um Lead existente e
 * acessível, e as demais decisões apenas encerram ou bloqueiam a conversa.
 */
export function executeLinkThread(
  stateInput: CommunicationCommandState,
  input: LinkThreadInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<LinkThreadValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const command = "communication.thread.link";
  let parsed: z.output<typeof linkThreadSchema>;
  try {
    parsed = linkThreadSchema.parse(normalizeCommunicationJson(input));
  } catch (error) {
    const message = error instanceof z.ZodError ? error.issues[0]?.message ?? "Decisão inválida." : "Decisão inválida.";
    return failure(stateInput, context, "validation_error", message);
  }

  const fingerprint = sanitizedFingerprint(parsed);
  const state = cloneState(stateInput);
  const replay = replayLookup<LinkThreadValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const current = state.threads.find((item) => item.id === parsed.threadId && item.ownerId === context.ownerId);
  if (!current) return failure(stateInput, context, "not_found", "Conversa não encontrada.");
  if (context.expectedVersion !== undefined && context.expectedVersion !== current.version)
    return failure(stateInput, context, "version_conflict", "A conversa foi alterada por outra operação.");

  const now = deps.now();
  let thread: CommunicationThread = { ...current, version: current.version + 1, updatedAt: now };
  let suppression: ChannelSuppression | undefined;
  let suppressions = state.channelSuppressions;

  if (parsed.decision === "attach_lead") {
    if (!parsed.leadId) return failure(stateInput, context, "validation_error", "Escolha o Lead que receberá a conversa.");
    const lead = state.leads.find((item) => item.id === parsed.leadId && !item.archived);
    if (!lead) return failure(stateInput, context, "not_found", "Lead não encontrado ou indisponível.");
    const membership = state.memberships.find((item) => item.leadId === lead.id);
    thread = {
      ...thread,
      linkState: "linked",
      linkReason: "manual",
      leadId: lead.id,
      companyId: lead.companyId,
      contactId: lead.primaryContactId,
      funnelId: membership?.funnelId,
      state: "open",
    };
  } else if (parsed.decision === "not_commercial") {
    thread = { ...thread, linkState: "not_commercial", linkReason: "manual", needsReply: false, state: "resolved" };
  } else if (parsed.decision === "block_sender") {
    thread = { ...thread, linkState: "blocked", linkReason: "manual", needsReply: false, state: "resolved" };
    if (current.counterpartAddress) {
      const existing = findSuppression(
        suppressions.filter((item) => item.ownerId === context.ownerId),
        { normalizedAddress: current.counterpartAddress },
      );
      if (existing) suppression = existing;
      else {
        suppression = {
          id: deps.uuid(),
          ownerId: context.ownerId,
          channel: EMAIL_CHANNEL,
          normalizedAddress: current.counterpartAddress,
          scope: "global",
          reason: "manual_block",
          createdAt: now,
        };
        suppressions = [...suppressions, suppression];
      }
    }
  } else {
    thread = { ...thread, linkState: "unlinked", linkReason: "manual", state: "open" };
  }

  return commit<LinkThreadValue>({
    state: {
      ...state,
      threads: state.threads.map((item) => (item.id === thread.id ? thread : item)),
      channelSuppressions: suppressions,
    },
    context,
    command,
    fingerprint,
    targetType: "communication_thread",
    targetId: thread.id,
    value: { thread, suppression },
    version: thread.version,
    previousVersion: current.version,
    before: { linkState: current.linkState, leadId: current.leadId },
    after: { linkState: thread.linkState, leadId: thread.leadId },
    deps,
  });
}

export const threadStateSchema = z.object({
  threadId: z.string().trim().min(1),
  transition: z.enum([
    "snooze",
    "resume",
    "resolve",
    "reopen",
    "mark_read",
    "mark_unread",
    "trash",
    "restore",
    "star",
    "unstar",
  ]),
  snoozeUntil: z.string().datetime({ offset: true }).optional(),
});

export type ThreadStateInput = z.input<typeof threadStateSchema>;

export interface ThreadStateValue {
  thread: CommunicationThread;
}

/** Adiar/retomar e resolver/reabrir persistem os dois lados da operação. */
export function executeThreadTransition(
  stateInput: CommunicationCommandState,
  input: ThreadStateInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<ThreadStateValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const command = "communication.thread.transition";
  let parsed: z.output<typeof threadStateSchema>;
  try {
    parsed = threadStateSchema.parse(normalizeCommunicationJson(input));
  } catch (error) {
    const message = error instanceof z.ZodError ? error.issues[0]?.message ?? "Transição inválida." : "Transição inválida.";
    return failure(stateInput, context, "validation_error", message);
  }
  if (parsed.transition === "snooze" && !parsed.snoozeUntil)
    return failure(stateInput, context, "validation_error", "Informe até quando a conversa ficará adiada.");

  const fingerprint = sanitizedFingerprint(parsed);
  const state = cloneState(stateInput);
  const replay = replayLookup<ThreadStateValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const current = state.threads.find((item) => item.id === parsed.threadId && item.ownerId === context.ownerId);
  if (!current) return failure(stateInput, context, "not_found", "Conversa não encontrada.");
  if (context.expectedVersion !== undefined && context.expectedVersion !== current.version)
    return failure(stateInput, context, "version_conflict", "A conversa foi alterada por outra operação.");

  const now = deps.now();
  if (parsed.transition === "snooze" && current.state === "snoozed")
    return failure(stateInput, context, "invalid_state", "A conversa já está adiada.");
  if (parsed.transition === "resume" && current.state !== "snoozed")
    return failure(stateInput, context, "invalid_state", "Somente conversas adiadas podem ser retomadas.");
  if (parsed.transition === "resolve" && current.state === "resolved")
    return failure(stateInput, context, "invalid_state", "A conversa já está resolvida.");
  if (parsed.transition === "reopen" && current.state !== "resolved")
    return failure(stateInput, context, "invalid_state", "Somente conversas resolvidas podem ser reabertas.");
  if (parsed.transition === "trash" && current.trashedAt)
    return failure(stateInput, context, "invalid_state", "A conversa já está na lixeira.");
  if (parsed.transition === "restore" && !current.trashedAt)
    return failure(stateInput, context, "invalid_state", "Somente conversas na lixeira podem ser restauradas.");
  if (parsed.transition === "star" && current.starredAt)
    return failure(stateInput, context, "invalid_state", "A conversa já está nos favoritos.");
  if (parsed.transition === "unstar" && !current.starredAt)
    return failure(stateInput, context, "invalid_state", "A conversa não está nos favoritos.");

  const patch: Partial<CommunicationThread> =
    parsed.transition === "snooze"
      ? { state: "snoozed", snoozedUntil: parsed.snoozeUntil }
      : parsed.transition === "resume"
        ? { state: "open", snoozedUntil: undefined }
        : parsed.transition === "resolve"
          ? { state: "resolved", needsReply: false, unread: false, snoozedUntil: undefined }
          : parsed.transition === "reopen"
            ? { state: "open", needsReply: true }
            : parsed.transition === "mark_read"
              ? { unread: false }
              : parsed.transition === "mark_unread"
                ? { unread: true }
                : parsed.transition === "trash"
                  ? // Descartar preserva mensagens e rascunho: só tira das listas.
                    { trashedAt: now }
                  : parsed.transition === "restore"
                    ? { trashedAt: undefined }
                    : parsed.transition === "star"
                      ? { starredAt: now }
                      : { starredAt: undefined };

  const thread: CommunicationThread = { ...current, ...patch, version: current.version + 1, updatedAt: now };

  return commit<ThreadStateValue>({
    state: { ...state, threads: state.threads.map((item) => (item.id === thread.id ? thread : item)) },
    context,
    command,
    fingerprint,
    targetType: "communication_thread",
    targetId: thread.id,
    value: { thread },
    version: thread.version,
    previousVersion: current.version,
    before: { state: current.state, unread: current.unread, trashed: Boolean(current.trashedAt) },
    after: { state: thread.state, unread: thread.unread, trashed: Boolean(thread.trashedAt) },
    deps,
  });
}


/* ------------------------------------------------------------------------- */
/* Envio nos canais de chat                                                  */
/* ------------------------------------------------------------------------- */

export const CHAT_CHANNELS = ["whatsapp", "instagram", "messenger"] as const;
export type ChatChannel = (typeof CHAT_CHANNELS)[number];

/** Teto local por arquivo e por mensagem, como no protótipo. */
export const CHAT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const CHAT_ATTACHMENT_MAX_COUNT = 10;

export const sendChannelMessageSchema = z
  .object({
    threadId: z.string().trim().min(1).optional(),
    /** Primeira mensagem: a conversa nasce do cadastro e do canal escolhido. */
    leadId: z.string().trim().min(1).optional(),
    channel: z.enum(CHAT_CHANNELS).optional(),
    bodyText: z.string().trim().max(20_000).default(""),
    /** Citação: a mensagem respondida é sempre da mesma conversa. */
    replyToId: z.string().trim().min(1).optional(),
    /**
     * Anexos como metadados (§27.3): nome, tipo declarado, tamanho e hash. O
     * conteúdo do arquivo não entra no domínio enquanto não houver Storage
     * privado e verificação — guardar o byte aqui seria prometer entrega.
     */
    attachments: z
      .array(
        z.object({
          filename: z.string().trim().min(1).max(255),
          declaredMimeType: z.string().trim().min(1).max(255),
          size: z.number().int().nonnegative().max(CHAT_ATTACHMENT_MAX_BYTES, "O limite local é de 10 MB por arquivo."),
          contentHash: z.string().trim().min(1).max(128),
        }),
      )
      .max(CHAT_ATTACHMENT_MAX_COUNT, "A mensagem comporta até 10 anexos.")
      .default([]),
  })
  .refine((value) => Boolean(value.threadId) || Boolean(value.leadId && value.channel), {
    message: "Escolha a conversa ou o cadastro e o canal.",
  })
  // Só anexo já é mensagem; texto vazio sem anexo nenhum não é.
  .refine((value) => value.bodyText.trim().length > 0 || (value.attachments ?? []).length > 0, {
    message: "Escreva a mensagem",
  });

/** Endpoint do cadastro em cada canal de chat; sem ele não há para onde falar. */
export function leadChannelEndpoint(lead: Lead, channel: ChatChannel): string | undefined {
  const raw = channel === "whatsapp" ? lead.phone : channel === "instagram" ? lead.instagram : undefined;
  const value = typeof raw === "string" ? raw.trim() : "";
  if (!value) return undefined;
  return channel === "instagram" ? value.replace(/^@/, "").toLowerCase() : value;
}

export type SendChannelMessageInput = z.input<typeof sendChannelMessageSchema>;

export interface SendChannelMessageValue {
  message: CommunicationMessage;
  thread: CommunicationThread;
  delivery: MessageDelivery;
}

/**
 * Mensagem enviada em WhatsApp, Instagram ou Messenger.
 *
 * O e-mail tem cadeia própria — rascunho, fila, janela, aquecimento, teto
 * diário, Message-ID e cópia em Enviados — e continua passando por ela. Os
 * canais de chat não têm nada disso: não há caixa postal, cabeçalho, pasta
 * remota nem transporte. Por isso o envio é um comando único, e a tentativa
 * nasce `simulated`: nenhum conteúdo sai daqui (§27.3). Somente `live`, que
 * exige conexão real e revisão humana, poderia contar como interação real
 * (§7.7) — e ela não existe neste ambiente.
 */
export function executeSendChannelMessage(
  stateInput: CommunicationCommandState,
  input: SendChannelMessageInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<SendChannelMessageValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const command = "communication.channel.send";
  let parsed: z.output<typeof sendChannelMessageSchema>;
  try {
    parsed = sendChannelMessageSchema.parse(normalizeCommunicationJson(input));
  } catch (error) {
    const message =
      error instanceof z.ZodError ? error.issues[0]?.message ?? "Mensagem inválida." : "Mensagem inválida.";
    return failure(stateInput, context, "validation_error", message);
  }

  const fingerprint = sanitizedFingerprint({
    threadId: parsed.threadId,
    replyToId: parsed.replyToId,
    bodyText: contentHash([parsed.bodyText, ...parsed.attachments.map((item) => item.contentHash)]),
  });
  const state = cloneState(stateInput);
  const replay = replayLookup<SendChannelMessageValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const now = deps.now();
  let created = false;
  let current = parsed.threadId
    ? state.threads.find((item) => item.id === parsed.threadId && item.ownerId === context.ownerId)
    : undefined;
  if (parsed.threadId && !current)
    return failure(stateInput, context, "not_found", "Conversa não encontrada.");

  if (!current && parsed.leadId && parsed.channel) {
    const channel = parsed.channel;
    const lead = state.leads.find((item) => item.id === parsed.leadId && !item.archived);
    if (!lead) return failure(stateInput, context, "not_found", "Cadastro não encontrado.");
    const endpoint = leadChannelEndpoint(lead, channel);
    if (!endpoint)
      return failure(
        stateInput,
        context,
        "validation_error",
        channel === "messenger"
          ? "O cadastro não tem um perfil de Messenger registrado."
          : channel === "whatsapp"
            ? "O cadastro não tem telefone para o WhatsApp."
            : "O cadastro não tem perfil do Instagram.",
      );
    // Reusar a conversa viva do mesmo cadastro e canal evita duas conversas
    // paralelas para o mesmo endpoint.
    current = state.threads.find(
      (item) =>
        item.ownerId === context.ownerId &&
        item.channel === channel &&
        item.leadId === lead.id &&
        !item.trashedAt,
    );
    if (!current) {
      const membership = state.memberships.find((item) => item.leadId === lead.id);
      current = {
        id: deps.uuid(),
        ownerId: context.ownerId,
        channel,
        mailboxId: `box-${channel}`,
        companyId: lead.companyId,
        contactId: lead.primaryContactId,
        leadId: lead.id,
        funnelId: membership?.funnelId,
        subject: lead.name,
        normalizedSubject: lead.name.trim().toLowerCase(),
        counterpartAddress: endpoint,
        linkState: "linked",
        linkReason: "manual",
        state: "open",
        needsReply: false,
        unread: false,
        lastMessageAt: now,
        version: 1,
        createdAt: now,
        updatedAt: now,
      };
      created = true;
    }
  }
  if (!current) return failure(stateInput, context, "not_found", "Conversa não encontrada.");
  if (current.channel === EMAIL_CHANNEL)
    return failure(
      stateInput,
      context,
      "invalid_state",
      "Conversas de e-mail são enviadas pelo rascunho e pela fila de envio.",
    );
  if (context.expectedVersion !== undefined && context.expectedVersion !== current.version)
    return failure(stateInput, context, "version_conflict", "A conversa foi alterada por outra operação.");
  if (current.trashedAt)
    return failure(stateInput, context, "invalid_state", "Restaure a conversa antes de responder.");
  if (current.state !== "open")
    return failure(stateInput, context, "invalid_state", "Reabra a conversa antes de responder.");
  if (current.linkState === "blocked" || current.linkState === "not_commercial")
    return failure(stateInput, context, "invalid_state", "A classificação da conversa bloqueia novos envios.");

  // Supressão vale para qualquer canal e vem antes de qualquer escrita.
  const suppression = findSuppression(state.channelSuppressions, {
    normalizedAddress: current.counterpartAddress,
    funnelId: current.funnelId,
    channel: current.channel,
  });
  if (suppression)
    return failure(
      stateInput,
      context,
      "forbidden",
      "O contato pediu para não receber mensagens neste canal.",
    );

  // A citação é verificada antes de escrever: responder a uma mensagem de
  // outra conversa, ou a uma já excluída, não é erro silencioso.
  if (parsed.replyToId) {
    const quoted = state.messages.find(
      (item) =>
        item.id === parsed.replyToId && item.ownerId === context.ownerId && item.threadId === current.id,
    );
    if (!quoted || quoted.deletedAt)
      return failure(stateInput, context, "validation_error", "A mensagem citada não está nesta conversa.");
  }

  const messageId = deps.uuid();
  const message: CommunicationMessage = {
    id: messageId,
    ownerId: context.ownerId,
    threadId: current.id,
    replyToId: parsed.replyToId,
    mailboxId: current.mailboxId,
    channel: current.channel,
    direction: "outbound",
    folder: "sent",
    externalMessageId: `local-${messageId}`,
    references: [],
    fromAddress: "cosmos",
    fromName: "COSMOS",
    toAddresses: [current.counterpartAddress],
    ccAddresses: [],
    subject: "",
    bodyText: parsed.bodyText,
    bodyPreview: preview(parsed.bodyText),
    headers: {},
    state: "sent",
    occurredAt: now,
    importedAt: now,
    contentHash: contentHash([parsed.bodyText, now]),
    hasAttachments: parsed.attachments.length > 0,
    commandId: context.commandId,
    correlationId: context.correlationId,
    version: 1,
    createdAt: now,
    updatedAt: now,
  };

  const messageAttachments: MessageAttachmentMeta[] = parsed.attachments.map((attachment) => ({
    id: deps.uuid(),
    ownerId: context.ownerId,
    messageId: message.id,
    // O nome vem de fora: barra e contrabarra viram sublinhado para o arquivo
    // não fingir um caminho.
    filename: attachment.filename.replace(/[\\/]/g, "_"),
    declaredMimeType: attachment.declaredMimeType,
    size: attachment.size,
    contentHash: attachment.contentHash,
    scanState: "pending",
    createdAt: now,
    updatedAt: now,
  }));

  const delivery: MessageDelivery = {
    id: deps.uuid(),
    ownerId: context.ownerId,
    messageId: message.id,
    mailboxId: current.mailboxId,
    attempt: 1,
    state: "sent",
    deliveryMode: "simulated",
    idempotencyKey: `${command}:${message.id}`,
    sanitizedResponse: "Demonstração local: nenhuma mensagem saiu deste ambiente.",
    appendState: "not_required",
    occurredAt: now,
    createdAt: now,
    updatedAt: now,
  };

  const thread: CommunicationThread = {
    ...current,
    needsReply: false,
    unread: false,
    lastMessageAt: now,
    lastOutboundAt: now,
    version: created ? current.version : current.version + 1,
    updatedAt: now,
  };

  return commit<SendChannelMessageValue>({
    state: {
      ...state,
      threads: created
        ? [...state.threads, thread]
        : state.threads.map((item) => (item.id === thread.id ? thread : item)),
      messages: [...state.messages, message],
      messageAttachments: [...state.messageAttachments, ...messageAttachments],
      deliveries: [...state.deliveries, delivery],
    },
    context,
    command,
    fingerprint,
    targetType: "communication_message",
    targetId: message.id,
    value: { message, thread, delivery },
    version: message.version,
    previousVersion: 0,
    after: { channel: message.channel, deliveryMode: delivery.deliveryMode },
    deps,
  });
}

/* ------------------------------------------------------------------------- */
/* Marcas e exclusão de mensagem dentro da conversa                          */
/* ------------------------------------------------------------------------- */

export const MESSAGE_MARKS = ["starred", "pinned"] as const;
export type MessageMark = (typeof MESSAGE_MARKS)[number];

export const markChannelMessageSchema = z.object({
  messageId: z.string().trim().min(1),
  mark: z.enum(MESSAGE_MARKS),
  /** Estado desejado, não alternância: repetir o comando não desmarca sozinho. */
  value: z.boolean(),
});
export type MarkChannelMessageInput = z.input<typeof markChannelMessageSchema>;

export const deleteChannelMessageSchema = z.object({ messageId: z.string().trim().min(1) });
export type DeleteChannelMessageInput = z.input<typeof deleteChannelMessageSchema>;

export interface ChannelMessageValue {
  message: CommunicationMessage;
  thread: CommunicationThread;
}

/** Conversa na lixeira ou arquivada não aceita edição: só restauração. */
function editableThread(
  state: CommunicationCommandState,
  message: CommunicationMessage,
  ownerId: string,
): CommunicationThread | { reason: string } {
  const thread = state.threads.find((item) => item.id === message.threadId && item.ownerId === ownerId);
  if (!thread) return { reason: "Conversa não encontrada." };
  if (thread.trashedAt) return { reason: "Restaure a conversa da lixeira para usar esta ação." };
  if (thread.state === "resolved") return { reason: "Reabra a conversa para usar esta ação." };
  return thread;
}

/**
 * Favoritar e fixar uma mensagem.
 *
 * As duas marcas são do registro e não duplicam nada: a faixa de fixadas e o
 * menu de favoritas leem a mesma mensagem. Mensagem excluída não aceita marca
 * — marcar um conteúdo que não existe mais seria estado desonesto (§18).
 */
export function executeMarkChannelMessage(
  stateInput: CommunicationCommandState,
  input: MarkChannelMessageInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<ChannelMessageValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const command = "communication.message.mark";
  let parsed: z.output<typeof markChannelMessageSchema>;
  try {
    parsed = markChannelMessageSchema.parse(normalizeCommunicationJson(input));
  } catch {
    return failure(stateInput, context, "validation_error", "Marca inválida.");
  }

  const fingerprint = sanitizedFingerprint({
    messageId: parsed.messageId,
    mark: parsed.mark,
    value: parsed.value,
  });
  const state = cloneState(stateInput);
  const replay = replayLookup<ChannelMessageValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const current = state.messages.find(
    (item) => item.id === parsed.messageId && item.ownerId === context.ownerId,
  );
  if (!current) return failure(stateInput, context, "not_found", "Mensagem não encontrada.");
  if (current.deletedAt)
    return failure(stateInput, context, "invalid_state", "A mensagem foi excluída.");
  const thread = editableThread(state, current, context.ownerId);
  if ("reason" in thread)
    return failure(
      stateInput,
      context,
      thread.reason === "Conversa não encontrada." ? "not_found" : "invalid_state",
      thread.reason,
    );
  if (context.expectedVersion !== undefined && context.expectedVersion !== current.version)
    return failure(stateInput, context, "version_conflict", "A mensagem foi alterada por outra operação.");

  const now = deps.now();
  const message: CommunicationMessage =
    parsed.mark === "starred"
      ? { ...current, starredAt: parsed.value ? now : undefined, version: current.version + 1, updatedAt: now }
      : { ...current, pinnedAt: parsed.value ? now : undefined, version: current.version + 1, updatedAt: now };

  return commit<ChannelMessageValue>({
    state: {
      ...state,
      messages: state.messages.map((item) => (item.id === message.id ? message : item)),
    },
    context,
    command,
    fingerprint,
    targetType: "communication_message",
    targetId: message.id,
    value: { message, thread },
    version: message.version,
    previousVersion: current.version,
    after: { mark: parsed.mark, value: parsed.value },
    deps,
  });
}

/**
 * Excluir a mensagem apaga o conteúdo, não o registro.
 *
 * O histórico continua explicando a conversa: a bolha vira "Mensagem
 * excluída", as marcas caem junto e nenhuma resposta continua citando um texto
 * que não existe mais (§7.4).
 */
export function executeDeleteChannelMessage(
  stateInput: CommunicationCommandState,
  input: DeleteChannelMessageInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<ChannelMessageValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const command = "communication.message.delete";
  let parsed: z.output<typeof deleteChannelMessageSchema>;
  try {
    parsed = deleteChannelMessageSchema.parse(normalizeCommunicationJson(input));
  } catch {
    return failure(stateInput, context, "validation_error", "Mensagem inválida.");
  }

  const fingerprint = sanitizedFingerprint({ messageId: parsed.messageId });
  const state = cloneState(stateInput);
  const replay = replayLookup<ChannelMessageValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const current = state.messages.find(
    (item) => item.id === parsed.messageId && item.ownerId === context.ownerId,
  );
  if (!current) return failure(stateInput, context, "not_found", "Mensagem não encontrada.");
  const thread = editableThread(state, current, context.ownerId);
  if ("reason" in thread)
    return failure(
      stateInput,
      context,
      thread.reason === "Conversa não encontrada." ? "not_found" : "invalid_state",
      thread.reason,
    );
  if (current.deletedAt)
    return failure(stateInput, context, "invalid_state", "A mensagem já foi excluída.");
  if (context.expectedVersion !== undefined && context.expectedVersion !== current.version)
    return failure(stateInput, context, "version_conflict", "A mensagem foi alterada por outra operação.");

  const now = deps.now();
  const message: CommunicationMessage = {
    ...current,
    bodyText: "",
    bodyPreview: "",
    hasAttachments: false,
    starredAt: undefined,
    pinnedAt: undefined,
    replyToId: undefined,
    deletedAt: now,
    version: current.version + 1,
    updatedAt: now,
  };

  return commit<ChannelMessageValue>({
    state: {
      ...state,
      // Citação órfã não sobrevive: quem respondia a esta mensagem perde a
      // citação em vez de apontar para um texto apagado.
      messages: state.messages.map((item) =>
        item.id === message.id
          ? message
          : item.replyToId === message.id
            ? { ...item, replyToId: undefined }
            : item,
      ),
    },
    context,
    command,
    fingerprint,
    targetType: "communication_message",
    targetId: message.id,
    value: { message, thread },
    version: message.version,
    previousVersion: current.version,
    after: { deleted: true },
    deps,
  });
}

/* ------------------------------------------------------------------------- */
/* Modelos de mensagem: rascunho editável e versão publicada imutável         */
/* ------------------------------------------------------------------------- */

/**
 * O contrato de §27.3 é o motivo destes comandos existirem: um fluxo publicado
 * fixa a VERSÃO usada, e "a mais recente" não é referência reproduzível. Por
 * isso versão publicada nunca muda de conteúdo — editar cria um rascunho novo.
 */
const TEMPLATE_VARIABLE = /\{\{\s*([a-z0-9_.]+)\s*\}\}/gi;

/** Variáveis são derivadas do texto, nunca digitadas em paralelo a ele. */
export function templateVariables(subject: string, bodyText: string): string[] {
  const found = new Set<string>();
  for (const text of [subject, bodyText]) {
    for (const match of text.matchAll(TEMPLATE_VARIABLE)) {
      found.add(match[1].toLowerCase());
    }
  }
  return [...found].sort();
}

/**
 * O opt-out também é derivado do corpo.
 *
 * Um booleano declarado à mão diria "tem descadastro" em um texto que não tem —
 * exatamente o tipo de estado desonesto que o §3 proíbe.
 */
export function templateHasOptOut(bodyText: string): boolean {
  const normalized = bodyText
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
  return (
    normalized.includes("{{descadastrar}}") ||
    normalized.includes("descadastr") ||
    normalized.includes("cancelar o recebimento") ||
    normalized.includes("nao quer mais receber")
  );
}

const templateTextSchema = z.object({
  subject: z.string().trim().min(1, "O assunto é obrigatório.").max(200),
  bodyText: z.string().trim().min(1, "O corpo é obrigatório.").max(20000),
});

export const createTemplateSchema = templateTextSchema.extend({
  // A lista de canais é uma só: quando o Messenger entrou no domínio (§27.3),
  // um enum próprio aqui deixaria o modelo de fora sem ninguém perceber.
  channel: z.enum(COMMUNICATION_CHANNELS),
  name: z.string().trim().min(1, "Dê um nome ao modelo.").max(120),
  description: z.string().trim().max(400).optional(),
});

export type CreateTemplateInput = z.input<typeof createTemplateSchema>;

export const saveTemplateDraftSchema = templateTextSchema.extend({
  templateId: z.string().trim().min(1, "Modelo é obrigatório."),
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(400).optional(),
});

export type SaveTemplateDraftInput = z.input<typeof saveTemplateDraftSchema>;

export const publishTemplateVersionSchema = z.object({
  versionId: z.string().trim().min(1, "Versão é obrigatória."),
});

export type PublishTemplateVersionInput = z.input<typeof publishTemplateVersionSchema>;

export const archiveTemplateSchema = z.object({
  templateId: z.string().trim().min(1, "Modelo é obrigatório."),
  archived: z.boolean(),
});

export type ArchiveTemplateInput = z.input<typeof archiveTemplateSchema>;

export interface TemplateValue {
  template: MessageTemplate;
  version?: MessageTemplateVersion;
}

function ownedTemplate(
  state: CommunicationCommandState,
  context: CommandContext,
  templateId: string,
): MessageTemplate | undefined {
  return state.messageTemplates.find(
    (item) => item.id === templateId && item.ownerId === context.ownerId,
  );
}

function templateSnapshot(
  template: MessageTemplate,
  version?: MessageTemplateVersion,
): Record<string, unknown> {
  return {
    id: template.id,
    name: template.name,
    channel: template.channel,
    archived: template.archived,
    versionId: version?.id,
    version: version?.version,
    status: version?.status,
  };
}

export function executeCreateMessageTemplate(
  stateInput: CommunicationCommandState,
  input: CreateTemplateInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<TemplateValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const parsed = createTemplateSchema.safeParse(normalizeCommunicationJson(input));
  if (!parsed.success)
    return failure(
      stateInput,
      context,
      "validation_error",
      parsed.error.issues[0]?.message ?? "Modelo inválido.",
    );
  const draft = parsed.data;
  const fingerprint = sanitizedFingerprint(draft);
  const replay = replayLookup<TemplateValue>(
    stateInput,
    context,
    "communication.template.create",
    fingerprint,
  );
  if (replay) return replay;

  const duplicate = stateInput.messageTemplates.some(
    (item) =>
      item.ownerId === context.ownerId &&
      item.channel === draft.channel &&
      item.name.trim().toLowerCase() === draft.name.trim().toLowerCase(),
  );
  if (duplicate)
    return failure(stateInput, context, "invalid_state", "Já existe um modelo com este nome.");

  const now = deps.now();
  const template: MessageTemplate = {
    id: deps.uuid(),
    ownerId: context.ownerId,
    channel: draft.channel,
    name: draft.name,
    description: draft.description || undefined,
    archived: false,
    createdAt: now,
    updatedAt: now,
  };
  const version: MessageTemplateVersion = {
    id: deps.uuid(),
    ownerId: context.ownerId,
    templateId: template.id,
    version: 1,
    status: "draft",
    subject: draft.subject,
    bodyText: draft.bodyText,
    variables: templateVariables(draft.subject, draft.bodyText),
    optOutIncluded: templateHasOptOut(draft.bodyText),
    createdAt: now,
  };
  return commit<TemplateValue>({
    state: {
      ...stateInput,
      messageTemplates: [...stateInput.messageTemplates, template],
      messageTemplateVersions: [...stateInput.messageTemplateVersions, version],
    },
    context,
    command: "communication.template.create",
    fingerprint,
    targetType: "message_template",
    targetId: template.id,
    value: { template, version },
    version: 1,
    previousVersion: 0,
    after: templateSnapshot(template, version),
    deps,
  });
}

/**
 * Salva o rascunho do modelo.
 *
 * Se a última versão já está publicada, um rascunho NOVO é aberto em cima dela:
 * o texto publicado continua exatamente como os fluxos que o fixaram esperam.
 */
export function executeSaveTemplateDraft(
  stateInput: CommunicationCommandState,
  input: SaveTemplateDraftInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<TemplateValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const parsed = saveTemplateDraftSchema.safeParse(normalizeCommunicationJson(input));
  if (!parsed.success)
    return failure(
      stateInput,
      context,
      "validation_error",
      parsed.error.issues[0]?.message ?? "Modelo inválido.",
    );
  const draft = parsed.data;
  const fingerprint = sanitizedFingerprint(draft);
  const replay = replayLookup<TemplateValue>(
    stateInput,
    context,
    "communication.template.save_draft",
    fingerprint,
  );
  if (replay) return replay;

  const template = ownedTemplate(stateInput, context, draft.templateId);
  if (!template) return failure(stateInput, context, "not_found", "Modelo não encontrado.");
  if (template.archived)
    return failure(stateInput, context, "invalid_state", "Modelo arquivado não recebe edição.");

  const versions = stateInput.messageTemplateVersions.filter(
    (item) => item.templateId === template.id && item.ownerId === context.ownerId,
  );
  const current = versions.find((item) => item.status === "draft");
  const highest = versions.reduce((max, item) => Math.max(max, item.version), 0);
  const now = deps.now();
  const version: MessageTemplateVersion = current
    ? {
        ...current,
        subject: draft.subject,
        bodyText: draft.bodyText,
        variables: templateVariables(draft.subject, draft.bodyText),
        optOutIncluded: templateHasOptOut(draft.bodyText),
      }
    : {
        id: deps.uuid(),
        ownerId: context.ownerId,
        templateId: template.id,
        version: highest + 1,
        status: "draft",
        subject: draft.subject,
        bodyText: draft.bodyText,
        variables: templateVariables(draft.subject, draft.bodyText),
        optOutIncluded: templateHasOptOut(draft.bodyText),
        createdAt: now,
      };
  const updated: MessageTemplate = {
    ...template,
    name: draft.name ?? template.name,
    description: draft.description ?? template.description,
    updatedAt: now,
  };
  return commit<TemplateValue>({
    state: {
      ...stateInput,
      messageTemplates: stateInput.messageTemplates.map((item) =>
        item.id === template.id ? updated : item,
      ),
      messageTemplateVersions: current
        ? stateInput.messageTemplateVersions.map((item) =>
            item.id === version.id ? version : item,
          )
        : [...stateInput.messageTemplateVersions, version],
    },
    context,
    command: "communication.template.save_draft",
    fingerprint,
    targetType: "message_template",
    targetId: template.id,
    value: { template: updated, version },
    version: version.version,
    previousVersion: current?.version ?? highest,
    before: templateSnapshot(template, current),
    after: templateSnapshot(updated, version),
    deps,
  });
}

export function executePublishTemplateVersion(
  stateInput: CommunicationCommandState,
  input: PublishTemplateVersionInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<TemplateValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const parsed = publishTemplateVersionSchema.safeParse(normalizeCommunicationJson(input));
  if (!parsed.success)
    return failure(
      stateInput,
      context,
      "validation_error",
      parsed.error.issues[0]?.message ?? "Versão inválida.",
    );
  const fingerprint = sanitizedFingerprint(parsed.data);
  const replay = replayLookup<TemplateValue>(
    stateInput,
    context,
    "communication.template.publish",
    fingerprint,
  );
  if (replay) return replay;

  const current = stateInput.messageTemplateVersions.find(
    (item) => item.id === parsed.data.versionId && item.ownerId === context.ownerId,
  );
  if (!current) return failure(stateInput, context, "not_found", "Versão não encontrada.");
  if (current.status !== "draft")
    return failure(stateInput, context, "invalid_state", "Esta versão já está publicada.");
  const template = ownedTemplate(stateInput, context, current.templateId);
  if (!template) return failure(stateInput, context, "not_found", "Modelo não encontrado.");
  if (template.archived)
    return failure(stateInput, context, "invalid_state", "Modelo arquivado não publica versão.");

  const now = deps.now();
  const version: MessageTemplateVersion = { ...current, status: "published", publishedAt: now };
  const updated: MessageTemplate = { ...template, updatedAt: now };
  // O aviso não bloqueia: uma resposta individual não exige descadastro, mas
  // usar este modelo em disparo automático exigiria (§27.3).
  const warnings = version.optOutIncluded
    ? []
    : ["Este modelo não tem opção de descadastro e não pode ser usado em envio automático."];
  return commit<TemplateValue>({
    state: {
      ...stateInput,
      messageTemplates: stateInput.messageTemplates.map((item) =>
        item.id === template.id ? updated : item,
      ),
      messageTemplateVersions: stateInput.messageTemplateVersions.map((item) =>
        item.id === version.id ? version : item,
      ),
    },
    context,
    command: "communication.template.publish",
    fingerprint,
    targetType: "message_template_version",
    targetId: version.id,
    value: { template: updated, version },
    version: version.version,
    previousVersion: current.version,
    warnings,
    before: templateSnapshot(template, current),
    after: templateSnapshot(updated, version),
    deps,
  });
}

/**
 * Arquiva ou restaura o modelo.
 *
 * Arquivar remove das listas ativas e NÃO apaga versão publicada: um fluxo que
 * fixou uma versão continua reproduzível (§18.1).
 */
export function executeArchiveMessageTemplate(
  stateInput: CommunicationCommandState,
  input: ArchiveTemplateInput,
  contextInput: CommandContext,
  deps: CommunicationCommandDeps,
): CommunicationExecution<TemplateValue> {
  const context = parseCommunicationCommandContext(contextInput);
  if (!context) return invalidCommunicationCommandContext(stateInput);
  const parsed = archiveTemplateSchema.safeParse(normalizeCommunicationJson(input));
  if (!parsed.success)
    return failure(
      stateInput,
      context,
      "validation_error",
      parsed.error.issues[0]?.message ?? "Modelo inválido.",
    );
  const fingerprint = sanitizedFingerprint(parsed.data);
  const replay = replayLookup<TemplateValue>(
    stateInput,
    context,
    "communication.template.archive",
    fingerprint,
  );
  if (replay) return replay;

  const template = ownedTemplate(stateInput, context, parsed.data.templateId);
  if (!template) return failure(stateInput, context, "not_found", "Modelo não encontrado.");
  if (template.archived === parsed.data.archived)
    return failure(
      stateInput,
      context,
      "invalid_state",
      parsed.data.archived ? "O modelo já está arquivado." : "O modelo já está ativo.",
    );

  const updated: MessageTemplate = {
    ...template,
    archived: parsed.data.archived,
    updatedAt: deps.now(),
  };
  return commit<TemplateValue>({
    state: {
      ...stateInput,
      messageTemplates: stateInput.messageTemplates.map((item) =>
        item.id === template.id ? updated : item,
      ),
    },
    context,
    command: "communication.template.archive",
    fingerprint,
    targetType: "message_template",
    targetId: template.id,
    value: { template: updated },
    version: 1,
    before: templateSnapshot(template),
    after: templateSnapshot(updated),
    deps,
  });
}

export interface CommunicationDemoAdapter {
  importMessage(input: ImportMessageInput, context: CommandContext): Promise<CommandResult<ImportMessageValue>>;
  saveDraft(input: SaveDraftInput, context: CommandContext): Promise<CommandResult<SaveDraftValue>>;
  queueSend(input: QueueSendInput, context: CommandContext): Promise<CommandResult<QueueSendValue>>;
  reconcileDelivery(input: ReconcileDeliveryInput, context: CommandContext): Promise<CommandResult<ReconcileDeliveryValue>>;
  linkThread(input: LinkThreadInput, context: CommandContext): Promise<CommandResult<LinkThreadValue>>;
  threadTransition(input: ThreadStateInput, context: CommandContext): Promise<CommandResult<ThreadStateValue>>;
  sendChannelMessage(
    input: SendChannelMessageInput,
    context: CommandContext,
  ): Promise<CommandResult<SendChannelMessageValue>>;
  markChannelMessage(
    input: MarkChannelMessageInput,
    context: CommandContext,
  ): Promise<CommandResult<ChannelMessageValue>>;
  deleteChannelMessage(
    input: DeleteChannelMessageInput,
    context: CommandContext,
  ): Promise<CommandResult<ChannelMessageValue>>;
  createTemplate(input: CreateTemplateInput, context: CommandContext): Promise<CommandResult<TemplateValue>>;
  saveTemplateDraft(input: SaveTemplateDraftInput, context: CommandContext): Promise<CommandResult<TemplateValue>>;
  publishTemplateVersion(input: PublishTemplateVersionInput, context: CommandContext): Promise<CommandResult<TemplateValue>>;
  archiveTemplate(input: ArchiveTemplateInput, context: CommandContext): Promise<CommandResult<TemplateValue>>;
  getState(): CommunicationCommandState;
  syncExternal(state: CommunicationCommandState): void;
}

/** Adapter local serializado: espelha o padrão já usado pelas tarefas. */
export function createCommunicationDemoAdapter(
  initialState: CommunicationCommandState,
  deps: CommunicationCommandDeps,
  onCommit?: (state: CommunicationCommandState) => void,
): CommunicationDemoAdapter {
  let state = cloneState(initialState);
  let queue = Promise.resolve();
  let busy = false;

  const enqueue = <T,>(operation: () => CommunicationExecution<T>): Promise<CommandResult<T>> => {
    const run = () => {
      busy = true;
      try {
        const execution = operation();
        if (execution.result.ok && execution.result.status === "applied") {
          state = execution.state;
          onCommit?.(state);
        }
        return execution.result;
      } finally {
        busy = false;
      }
    };
    if (!busy) return Promise.resolve(run());
    const next = queue.then(run);
    queue = next.then(() => undefined, () => undefined);
    return next;
  };

  return {
    importMessage: (input, context) => enqueue(() => executeImportMessage(state, input, context, deps)),
    saveDraft: (input, context) => enqueue(() => executeSaveDraft(state, input, context, deps)),
    queueSend: (input, context) => enqueue(() => executeQueueSend(state, input, context, deps)),
    reconcileDelivery: (input, context) => enqueue(() => executeReconcileDelivery(state, input, context, deps)),
    linkThread: (input, context) => enqueue(() => executeLinkThread(state, input, context, deps)),
    threadTransition: (input, context) => enqueue(() => executeThreadTransition(state, input, context, deps)),
    sendChannelMessage: (input, context) =>
      enqueue(() => executeSendChannelMessage(state, input, context, deps)),
    markChannelMessage: (input, context) =>
      enqueue(() => executeMarkChannelMessage(state, input, context, deps)),
    deleteChannelMessage: (input, context) =>
      enqueue(() => executeDeleteChannelMessage(state, input, context, deps)),
    createTemplate: (input, context) => enqueue(() => executeCreateMessageTemplate(state, input, context, deps)),
    saveTemplateDraft: (input, context) => enqueue(() => executeSaveTemplateDraft(state, input, context, deps)),
    publishTemplateVersion: (input, context) => enqueue(() => executePublishTemplateVersion(state, input, context, deps)),
    archiveTemplate: (input, context) => enqueue(() => executeArchiveMessageTemplate(state, input, context, deps)),
    getState: () => state,
    syncExternal: (nextState) => {
      if (!busy) state = cloneState(nextState);
    },
  };
}

export const EMAIL_DAILY_CAP = DAILY_SEND_CAP;
